// A Button that opens a hidden <input type="file"> and hands the chosen File
// back via onFile. Used by the F-F media tabs (custom-narration upload +
// per-kind media upload) so each upload affordance reads as a normal button
// rather than a raw file input. The hidden input is reset after each pick so
// the same file can be re-selected.

import { useRef, type ChangeEvent, type ReactNode } from "react";

import { Button } from "@shared/components/ui/button";

export interface FileButtonProps {
  accept: string;
  onFile: (file: File) => void;
  disabled?: boolean;
  testId: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  children: ReactNode;
}

const FileButton = ({
  accept,
  onFile,
  disabled,
  testId,
  variant = "outline",
  children,
}: FileButtonProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onFile(file);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        data-testid={testId}
      >
        {children}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
        data-testid={`${testId}-input`}
      />
    </>
  );
};

export default FileButton;
